import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TABS, TITLES } from 'locales/locales'
import { useAppSelector } from 'app/hooks'
import { IRoom, RoomTypes } from 'types/rooms.types'
import { IUserData } from 'types/user.types'

type UseRoomParams = {
  roomId?: string
}

export const filterUserNameFromRoomName = (
  roomName: string,
  type: string,
  userFullname: string
) => {
  if (roomName && type !== RoomTypes.CM) {
    return roomName
      .split(',')
      .filter(
        (name: string) =>
          !name?.toLowerCase()?.includes(userFullname?.toLowerCase())
      )
      .join(',')
  }
  return roomName
}

const getRoomAvatarUrl = (
  type: string,
  members: string[],
  myId: string,
  userData: IUserData,
  membersWithoutMe: string[],
) => {
  if (type === RoomTypes.GM) {
    return members.length.toString()
  } else if (type === RoomTypes.OTR) {
    if (membersWithoutMe.length === 0) {
      return userData[myId]?.avatar
    }
    if (membersWithoutMe.length === 1) {
      return userData[membersWithoutMe[0]]?.avatar
    }
    return membersWithoutMe.length.toString()
  }
  return membersWithoutMe
    .map((userId: string) => userData[userId]?.avatar)
    .join('')
}

const useRoom = (params?: UseRoomParams) => {
  const routeParams = useParams()
  const { t } = useTranslation()

  // 🔥 Öncelik: parametre → yoksa URL
  const roomId = params?.roomId ?? routeParams.roomId ?? ''

  const rooms = useAppSelector((state) => state.rooms.data)
  const myId = useAppSelector((state) => state.auth.id)

  const roomInfo = useMemo(() => {
    const {
      _id = "",
      createdAt,
      members = [],
      acceptingUsers,
      type,
      isVanishMode,
      createdByUserId,
      isAppear,
      isDeleted,
      pins = [],
      name,
      unreadCount = 0,
      userData = {}
    } = rooms.find((room: IRoom) => room._id === roomId) || {}

    if (!createdAt) return null

    const membersWithoutMe =
      type === RoomTypes.OTR
        ? members.filter((id: string) => id !== myId)
        : members.filter((id: string) =>
            members.length > 1 ? id !== myId : id === myId
          )

    const myRoomsId = rooms.find(
      (room: IRoom) =>
        room.members.length === 1 && !room.invitedUsers,
    )?._id

    let roomName = membersWithoutMe
      .map((userId: string) => userData[userId]?.fullname)
      .join(', ')

    if (members.length === 1 && type === "OTR") {
      roomName = t(TITLES.DEFAULT_OTR_ROOM_HEADER)
    }

    const description =
      type === RoomTypes.OTR || type === RoomTypes.GM
        ? members.length + ' ' + t(TABS.MEMBERS)
        : membersWithoutMe
            .map((userId: string) => userData[userId]?.title)
            .join('')

    const url = getRoomAvatarUrl(
      type,
      members,
      myId,
      userData,
      membersWithoutMe
    )

    const isMe = !!name
    
    return {
      _id,
      roomName,
      description,
      url,
      createdAt,
      isAppear,
      createdByUserId,
      isDeleted,
      isVanishMode,
      type,
      isOwnerMe: createdByUserId === myId,
      pins,
      members,
      acceptingUsers,
      isMe,
      myRoomsId,
      unreadCount,
      userData
    }
  }, [rooms, myId, roomId])

  return {
    room: roomInfo,
    myId,
    roomId,
    myRoomsId: roomInfo?.myRoomsId,
    users: roomInfo?.userData
  }
}

export default useRoom